import { useState, useEffect, useMemo, useRef } from 'react';
import { useApp } from '../../contexts/AppContext';
import { supabase, supabaseAdmin } from '../../supabaseClient';
import { Radio, Plus, AlertTriangle } from 'lucide-react';

const ACCENT = '#00e5a0';

export default function TrackingPage() {
  const { assets, facilities, antennas, profile, reload } = useApp();

  // Build antenna lookup: facilityId + port → location name
  const antennaLabel = useMemo(() => {
    const map = {};
    antennas.forEach(a => {
      const key = `${a.facility_id}_${a.antenna_port}`;
      map[key] = a.location || `ANT${a.antenna_port}`;
      if (!map[`_${a.antenna_port}`]) map[`_${a.antenna_port}`] = a.location || `ANT${a.antenna_port}`;
    });
    return map;
  }, [antennas]);

  // Portal is the source of truth for antenna names.
  // Look up the antenna by facility_id + port and use its portal-configured antenna_name.
  // Falls back to whatever the bridge wrote, then to ANT<port>.
  const antennaByFacPort = useMemo(() => {
    const map = {};
    antennas.forEach(a => {
      map[`${a.facility_id}_${a.antenna_port}`] = a;
    });
    return map;
  }, [antennas]);

  const getAntennaName = (m) => {
    if (!m.antenna_port) return null;
    const ant = antennaByFacPort[`${m.to_facility}_${m.antenna_port}`];
    if (ant?.antenna_name) return ant.antenna_name;
    if (ant?.location)     return ant.location;
    if (m.antenna_name)    return m.antenna_name;
    return `ANT${m.antenna_port}`;
  };

  // Reader label — prefer facility name (what the portal calls the "reader location"),
  // fall back to bridge-provided reader_name.
  const getReaderName = (m) => {
    const ant = m.antenna_port && antennaByFacPort[`${m.to_facility}_${m.antenna_port}`];
    if (ant?.bridge_ip) return `${resolveFacility(m.to_facility) || ''} · ${ant.bridge_ip}`;
    return m.reader_name || '—';
  };

  // Resolve a facility reference (could be an id OR a name) → canonical facility name.
  // Movements sometimes store the facility ID in from_facility/to_facility, so the feed
  // showed raw UUIDs and falsely flagged MOVED. Resolving both sides fixes that.
  const facLookup = useMemo(() => {
    const byId = {}, byName = {};
    facilities.forEach(f => { byId[String(f.id)] = f.name; byName[f.name] = f.name; });
    return { byId, byName };
  }, [facilities]);

  const resolveFacility = (ref) => {
    if (!ref) return null;
    return facLookup.byId[String(ref)] || facLookup.byName[ref] || ref;
  };

  // Dedupe antennas: one physical antenna often has BOTH a bridge-auto-created row and a
  // Facilities-page row — with DIFFERENT facility_id values (bridge writes a stale/unknown id).
  // The only field the duplicates share is location + antenna_port, so key on that.
  // Prefer the row tied to a known facility, then Online, then more reads.
  const sidebarAntennas = useMemo(() => {
    const known = new Set(facilities.map(f => String(f.id)));
    const byZone = {};
    // Drop orphan antennas whose facility no longer exists
    antennas.filter(a => known.has(String(a.facility_id))).forEach(a => {
      const loc = (a.location || '').trim().toLowerCase();
      const key = `${loc || `id:${a.facility_id}`}_${a.antenna_port}`;
      const prev = byZone[key];
      if (!prev) { byZone[key] = a; return; }
      const aKnown = known.has(String(a.facility_id));
      const pKnown = known.has(String(prev.facility_id));
      if (aKnown !== pKnown) { if (aKnown) byZone[key] = a; return; }
      const aOnline = a.status === 'Online', pOnline = prev.status === 'Online';
      if (aOnline !== pOnline) { if (aOnline) byZone[key] = a; return; }
      if ((a.reads || 0) > (prev.reads || 0)) byZone[key] = a;
    });
    return Object.values(byZone);
  }, [antennas, facilities]);

  const handleSetFilter = (v) => { setFilter(v); setVisibleCount(50); };
  const handleSetSearch = (v) => { setSearch(v); setVisibleCount(50); };
  const [facilityFilter, setFacilityFilter] = useState('all');
  const [filter,       setFilter]       = useState('all');
  const [search,       setSearch]       = useState('');
  const [unknownTags,  setUnknownTags]  = useState([]);
  const [bridgeStatus, setBridgeStatus] = useState(null);
  const [assetMovements, setAssetMovements] = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState(null);
  const [diag,         setDiag]         = useState(null);
  const [liveCount,    setLiveCount]    = useState(0);
  const [visibleCount, setVisibleCount] = useState(50);
  const [historyRange, setHistoryRange] = useState('24h'); // '24h' default | 'recent' | '3d' | '7d'
  const [historyLoading, setHistoryLoading] = useState(false);
  // Feed mode: 'live' shows every scan; 'batch' snapshots every N minutes + dedupes repeats
  const [feedMode, setFeedMode] = useState('live');
  const [refreshMinutes, setRefreshMinutes] = useState(15);
  const [feedSnapshot, setFeedSnapshot] = useState([]);
  const [nextTickAt, setNextTickAt] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [rtStatus, setRtStatus] = useState(window.__rfidRealtimeStatus || 'connecting');
  const [registerEpc, setRegisterEpc] = useState(null);
  const [registerForm, setRegisterForm] = useState({ name: '', type: 'Equipment', facility_id: '' });
  const [registering, setRegistering] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const bridgeUrl = localStorage.getItem('rfid_bridge_url') || 'http://localhost:3500';
  const debugMode = localStorage.getItem('rfid_debug') === '1';
  const feedRef = useRef(null);

  // Watch global realtime status set by AppContext
  useEffect(() => {
    const t = setInterval(() => setRtStatus(window.__rfidRealtimeStatus || 'connecting'), 3000);
    return () => clearInterval(t);
  }, []);

  // ── Feed-mode snapshot logic ──────────────────────────────────────────────
  // Live: snapshot always mirrors assetMovements immediately.
  // Batch: snapshot is refreshed every refreshMinutes; new tags land in DB in real time
  //        but the UI stays quiet until the next tick.
  const assetMovementsRef = useRef(assetMovements);
  useEffect(() => { assetMovementsRef.current = assetMovements; }, [assetMovements]);

  useEffect(() => {
    if (feedMode === 'live') {
      setFeedSnapshot(assetMovements);
      setNextTickAt(null);
      return;
    }
    setFeedSnapshot(assetMovements);
    setNextTickAt(Date.now() + refreshMinutes * 60_000);
    const timer = setInterval(() => {
      setFeedSnapshot(assetMovementsRef.current);
      setNextTickAt(Date.now() + refreshMinutes * 60_000);
    }, refreshMinutes * 60_000);
    return () => clearInterval(timer);
  }, [feedMode, refreshMinutes]); // eslint-disable-line

  // Keep snapshot in sync while in live mode
  useEffect(() => {
    if (feedMode === 'live') setFeedSnapshot(assetMovements);
  }, [feedMode, assetMovements]);

  // Ticker for the "next update in Xm Ys" countdown
  useEffect(() => {
    if (feedMode !== 'batch') return;
    const t = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(t);
  }, [feedMode]);

  useEffect(() => {
    if (!profile?.org_id) return;
    loadData();
    fetchBridgeStatus();
    const ch = supabase.channel('tracking-live')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'asset_movements' }, (payload) => {
        if (payload.new?.org_id !== profile.org_id) return;
        setAssetMovements(prev => [payload.new, ...prev]); // don't trim — history mode may hold 5000+
        setLiveCount(n => n + 1);
        if (feedRef.current) {
          feedRef.current.style.boxShadow = `0 0 0 2px ${ACCENT}`;
          setTimeout(() => { if (feedRef.current) feedRef.current.style.boxShadow = 'none'; }, 600);
        }
      })
      .on('postgres_changes', { event: '*', schema: 'public', table: 'unknown_tags' }, (payload) => {
        if (payload.new?.org_id !== profile.org_id) return;
        loadUnknownTags();
      })
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, [profile?.org_id]);

  const loadData = async (range = historyRange) => {
    if (!profile?.org_id) return;
    if (range === 'recent') setLoading(true); else setHistoryLoading(true);
    setLoadError(null);
    setDiag(null);

    let mvs = null, mvErr = null;

    if (range === 'recent') {
      // Latest 100 rows — fast, real-time feel
      const r = await supabase
        .from('asset_movements')
        .select('*')
        .eq('org_id', profile.org_id)
        .order('detected_at', { ascending: false })
        .limit(100);
      mvs = r.data; mvErr = r.error;
    } else {
      // Wider range: use sample_movements() RPC — 1 row per asset per hour
      const hoursMap = { '24h': 24, '3d': 24*3, '7d': 24*7 };
      const hours = hoursMap[range] || 24;
      const since = new Date(Date.now() - hours * 3600 * 1000).toISOString();
      const r = await supabase.rpc('sample_movements', {
        p_org_id: profile.org_id,
        p_since:  since,
        p_max_rows: 20000,
      });
      mvs = r.data; mvErr = r.error;
      // Sort desc for the feed (RPC returns per-asset-hour sort)
      if (mvs) mvs.sort((a, b) => new Date(b.detected_at) - new Date(a.detected_at));
    }

    if (mvErr) {
      console.error('[Tracking] asset_movements error:', mvErr);
      setLoadError(mvErr.message);
    } else {
      setAssetMovements(mvs || []);
      // Diagnostic (debug mode only): if nothing for this org, check whether rows exist under
      // other org_ids. Must use the service-role client — RLS would otherwise restrict this to our own org.
      if (debugMode && (mvs || []).length === 0) {
        const probe = supabaseAdmin || supabase;   // admin bypasses RLS; plain client can't see other orgs
        const { data: anyRows } = await probe
          .from('asset_movements')
          .select('org_id, detected_at')
          .order('detected_at', { ascending: false })
          .limit(50);
        const otherOrgs = [...new Set((anyRows || []).map(r => r.org_id))].filter(Boolean);
        console.warn('[Tracking] No movements for org', profile.org_id,
          '— recent rows belong to org_ids:', otherOrgs, '(admin probe:', !!supabaseAdmin, ')');
        setDiag({
          myOrg: profile.org_id,
          otherOrgs,
          sampleCount: (anyRows || []).length,
          usedAdmin: !!supabaseAdmin,
        });
      }
    }

    await loadUnknownTags();
    setLoading(false);
    setHistoryLoading(false);
  };

  const loadHistory = (range) => {
    setHistoryRange(range);
    setVisibleCount(50);
    loadData(range);
  };

  const loadUnknownTags = async () => {
    if (!profile?.org_id) return;
    const { data, error } = await supabase
      .from('unknown_tags')
      .select('*')
      .eq('org_id', profile.org_id)
      .order('last_seen', { ascending: false })
      .limit(100);
    if (error) console.warn('[Tracking] unknown_tags error:', error.message);
    if (data) setUnknownTags(data);
  };

  const openRegister = (epc) => {
    setRegisterEpc(epc);
    setRegisterForm({ name: '', type: 'Equipment', facility_id: '' });
    setRegisterError('');
  };

  const doRegister = async () => {
    if (!registerForm.name.trim()) { setRegisterError('Asset name is required'); return; }
    setRegistering(true);
    setRegisterError('');
    try {
      const facility = facilities.find(f => f.id === registerForm.facility_id);
      const { error } = await supabase.from('assets').insert({
        org_id:                profile.org_id,
        name:                  registerForm.name.trim(),
        type:                  registerForm.type,
        epc:                   registerEpc,
        status:                'Available',
        condition:             'Good',
        current_facility:      registerForm.facility_id || null,
        current_facility_name: facility?.name || null,
      });
      if (error) { setRegisterError(error.message); return; }
      await supabase.from('unknown_tags').delete().eq('epc', registerEpc).eq('org_id', profile.org_id);
      setUnknownTags(prev => prev.filter(t => t.epc !== registerEpc));
      setRegisterEpc(null);
      reload();
    } catch (e) {
      setRegisterError(e.message);
    } finally {
      setRegistering(false);
    }
  };

  const fetchBridgeStatus = async () => {
    if (!localStorage.getItem('rfid_bridge_url')) { setBridgeStatus(null); return; }
    try {
      const res = await fetch(`${bridgeUrl}/api/status`, { signal: AbortSignal.timeout(2000) });
      if (!res.ok) { setBridgeStatus(null); return; }
      setBridgeStatus(await res.json());
    } catch { setBridgeStatus(null); }
  };

  // Dedup: in batch mode, collapse repeated scans (same asset + facility + antenna) within window
  const dedupedSnapshot = useMemo(() => {
    if (feedMode === 'live') return feedSnapshot;
    const windowMs = refreshMinutes * 60_000;
    const result = [];
    const lastByKey = new Map();
    for (const m of feedSnapshot) {
      const key = `${m.asset_id || m.epc}_${m.to_facility}_${m.antenna_port}`;
      const idx = lastByKey.get(key);
      if (idx !== undefined) {
        const prev = result[idx];
        const diff = Math.abs(new Date(prev.detected_at).getTime() - new Date(m.detected_at).getTime());
        if (diff < windowMs) {
          prev._seenCount = (prev._seenCount || 1) + 1;
          prev._firstSeenAt = m.detected_at; // older than newest kept row
          continue;
        }
      }
      result.push({ ...m, _seenCount: 1 });
      lastByKey.set(key, result.length - 1);
    }
    return result;
  }, [feedSnapshot, feedMode, refreshMinutes]);

  // Filter movements
  const filtered = useMemo(() => {
    let list = dedupedSnapshot;
    if (facilityFilter !== 'all') {
      const facName = facilities.find(f => f.id === facilityFilter)?.name || facilityFilter;
      list = list.filter(m =>
        m.to_facility === facilityFilter ||
        m.to_facility === facName ||
        m.to_facility_name === facName ||
        m.facility_name === facName ||
        m.facility_id === facilityFilter
      );
    }
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(m =>
        m.asset_name?.toLowerCase().includes(q) ||
        m.epc?.toLowerCase().includes(q) ||
        m.to_facility_name?.toLowerCase().includes(q) ||
        m.reader_name?.toLowerCase().includes(q)
      );
    }
    return list;
  }, [dedupedSnapshot, facilityFilter, search]);

  // Enrich each movement with the previous position for its asset
  // so the row can show "FacA · ANT1 → FacB · ANT2" even when the bridge
  // only wrote to_* (no from_antenna_port column exists).
  const filteredWithPrev = useMemo(() => {
    // Walk oldest → newest per asset to track "last seen zone"
    const lastByAsset = new Map();
    const sortedAsc = [...filtered].sort((a, b) => new Date(a.detected_at) - new Date(b.detected_at));
    for (const m of sortedAsc) {
      const key = m.asset_id || m.epc;
      if (!key) continue;
      const prev = lastByAsset.get(key);
      if (prev) {
        m._prev_facility      = prev.to_facility;
        m._prev_facility_name = prev.to_facility_name;
        m._prev_antenna_port  = prev.antenna_port;
        m._prev_antenna_name  = prev.antenna_name;
      }
      lastByAsset.set(key, m);
    }
    return filtered; // filtered is desc; we mutated in-place
  }, [filtered]);

  // Stats
  const uniqueAssetsToday = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    return new Set(assetMovements.filter(m => m.detected_at?.startsWith(today)).map(m => m.asset_id)).size;
  }, [assetMovements]);

  const activeAntennas = antennas.filter(a => a.status === 'Online').length;

  const fmtTime = (ts) => {
    if (!ts) return '—';
    const d = new Date(ts);
    return d.toLocaleTimeString('en-MY', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const fmtAgo = (ts) => {
    if (!ts) return '';
    const sec = Math.floor((Date.now() - new Date(ts)) / 1000);
    if (sec < 60)   return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
    return `${Math.floor(sec / 86400)}d ago`;
  };

  return (
    <>
    <div style={{ padding: '0 0 40px' }}>

      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontWeight: 800, fontSize: 22, color: '#e8edf2' }}>
          <Radio size={20} style={{verticalAlign:'-4px',marginRight:8}}/>Live RFID Tracking
        </div>
        <div style={{ fontSize: 12, color: '#7090a0', marginTop: 4 }}>
          Real-time asset movement feed · {assetMovements.length} events loaded
        </div>
      </div>

      {/* Bridge Status */}
      {localStorage.getItem('rfid_bridge_url') && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
          background: '#060b10', border: `1px solid ${bridgeStatus ? '#00e5a030' : '#1a2a3a'}`,
          borderRadius: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ width: 8, height: 8, borderRadius: '50%',
            background: bridgeStatus ? '#00e5a0' : '#e05757',
            boxShadow: bridgeStatus ? '0 0 8px #00e5a0' : 'none' }}/>
          <span style={{ fontSize: 12, fontWeight: 700, color: bridgeStatus ? '#00e5a0' : '#a0b4c0' }}>
            {bridgeStatus ? 'Bridge Online' : 'Bridge Offline'}
          </span>
          {bridgeStatus && (
            <>
              <span style={{ fontSize: 11, color: '#7090a0' }}>·</span>
              <span style={{ fontSize: 11, color: '#7090a0' }}>
                {bridgeStatus.connectedReaders} reader{bridgeStatus.connectedReaders !== 1 ? 's' : ''} connected
              </span>
              <span style={{ fontSize: 11, color: '#7090a0' }}>·</span>
              <span style={{ fontSize: 11, color: '#7090a0' }}>
                {bridgeStatus.tagsToday} tags today
              </span>
              {bridgeStatus.readers?.map(r => (
                <span key={r.key} style={{ fontSize: 10, padding: '2px 8px', borderRadius: 20,
                  background: r.connected ? '#00e5a010' : '#e0575710',
                  border: `1px solid ${r.connected ? '#00e5a030' : '#e0575730'}`,
                  color: r.connected ? '#00e5a0' : '#e05757', fontWeight: 600 }}>
                  {r.name} {r.connected ? `(${r.tagsRead || 0})` : '●'}
                </span>
              ))}
            </>
          )}
          {!bridgeStatus && (
            <span style={{ fontSize: 11, color: '#7090a0' }}>
              Cannot reach bridge at {localStorage.getItem('rfid_bridge_url')}
            </span>
          )}
          <button onClick={() => { localStorage.removeItem('rfid_bridge_url'); setBridgeStatus(null); window.location.reload(); }}
            style={{ marginLeft: 'auto', fontSize: 11, padding: '3px 10px', borderRadius: 6,
              background: 'transparent', border: '1px solid #1a2a3a', color: '#7090a0',
              cursor: 'pointer' }}>
            Disconnect
          </button>
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { icon: '🏷', label: 'Total Events',    value: assetMovements.length,  c: ACCENT },
          { icon: '📦', label: 'Assets Today',    value: uniqueAssetsToday,       c: '#5b9cf6' },
          { icon: '⚠',  label: 'Unknown Tags',    value: unknownTags.length,      c: unknownTags.length > 0 ? '#f5a623' : '#a0b4c0' },
          { icon: '📡', label: 'Online Antennas', value: activeAntennas,          c: activeAntennas > 0 ? ACCENT : '#e05757' },
        ].map(s => (
          <div key={s.label} style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 10, color: '#7090a0', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: 6 }}>{s.icon} {s.label}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: s.c, fontFamily: "'Syne',sans-serif" }}>{s.value}</div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16, alignItems: 'start' }}>

        {/* Live Feed */}
        <div>
          {/* Filters */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
            <input
              style={{ flex: 1, minWidth: 180, background: '#060b10', border: '1px solid #1a2a3a',
                borderRadius: 8, padding: '8px 12px', fontSize: 12, color: '#c8d6e0', outline: 'none' }}
              placeholder="Search EPC, asset name, facility…"
              value={search} onChange={e => handleSetSearch(e.target.value)}
            />
            <select style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 8,
              padding: '8px 12px', fontSize: 12, color: '#c8d6e0', outline: 'none' }}
              value={facilityFilter} onChange={e => setFacilityFilter(e.target.value)}>
              <option value="all">All Facilities</option>
              {[...new Map(facilities.map(f => [f.name, f])).values()].map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
            <button style={{ background: '#1a2a3a', border: 'none', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, color: '#c8d6e0', cursor: 'pointer' }}
              onClick={() => loadData()}>↻ Refresh</button>
            <button style={{ background: '#5b9cf618', border: '1px solid #5b9cf640', borderRadius: 8,
              padding: '8px 14px', fontSize: 12, color: '#5b9cf6', cursor: 'pointer', fontWeight: 700 }}
              title="Force all bridges in this org to do an immediate inventory scan"
              onClick={async () => {
                if (!profile?.org_id) return;
                const { data: bridges } = await supabase.from('bridges').select('id,bridge_name').eq('org_id', profile.org_id);
                if (!bridges?.length) { alert('No bridges registered for this org.'); return; }
                await Promise.all(bridges.map(b =>
                  supabase.from('bridge_commands').insert({ bridge_id: b.id, command: 'scan_now', status: 'pending' })
                ));
                alert(`✓ Scan Now sent to ${bridges.length} bridge(s). Fresh scans will land within 10-15s.`);
              }}>🔍 Scan All Now</button>
          </div>

          {/* History range toolbar — load older records on demand */}
          <div style={{ display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap',padding:'8px 12px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8 }}>
            <span style={{ fontSize:11,color:'#7090a0',fontWeight:600 }}>History:</span>
            {[
              ['recent','Latest 100'],
              ['24h','Last 24h'],
              ['3d','Last 3 days'],
              ['7d','Last 7 days'],
            ].map(([r,label]) => {
              const active = historyRange === r;
              return (
                <button key={r} onClick={() => loadHistory(r)} disabled={historyLoading}
                  style={{ fontSize:11,padding:'5px 11px',borderRadius:6,
                    background: active ? '#5b9cf618' : 'transparent',
                    border: `1px solid ${active ? '#5b9cf640' : '#1a2a3a'}`,
                    color: active ? '#5b9cf6' : '#a0b4c0',
                    cursor: historyLoading ? 'wait' : 'pointer', fontWeight: active ? 700 : 500 }}>
                  {label}
                </button>
              );
            })}
            {historyLoading && <span style={{ fontSize:11,color:'#f5a623' }}>Loading history…</span>}
            {!historyLoading && (
              <span style={{ fontSize:11,color:'#7090a0',marginLeft:'auto' }}>
                {assetMovements.length} record{assetMovements.length===1?'':'s'} loaded
                {assetMovements.length > 0 && (
                  <> · oldest: {new Date(assetMovements[assetMovements.length-1].detected_at).toLocaleString('en-MY')}</>
                )}
                {historyRange !== 'recent' && (
                  <span style={{ marginLeft:8, color:'#5b9cf6' }} title="Sampled: 1 row per asset per hour for readability">📊 sampled</span>
                )}
              </span>
            )}
          </div>

          {/* Feed mode toggle + refresh interval */}
          <div style={{ display:'flex',gap:8,marginBottom:12,alignItems:'center',flexWrap:'wrap',padding:'8px 12px',background:'#060b10',border:'1px solid #1a2a3a',borderRadius:8 }}>
            <span style={{ fontSize:11,color:'#7090a0',fontWeight:600 }}>Feed:</span>
            {[['live','Live'],['batch','Batch']].map(([m,label]) => {
              const active = feedMode === m;
              return (
                <button key={m} onClick={() => setFeedMode(m)}
                  style={{ fontSize:11,padding:'5px 12px',borderRadius:6,
                    background: active ? '#00e5a018' : 'transparent',
                    border: `1px solid ${active ? '#00e5a040' : '#1a2a3a'}`,
                    color: active ? '#00e5a0' : '#a0b4c0',
                    cursor: 'pointer', fontWeight: active ? 700 : 500 }}>
                  {m === 'live' ? '● ' : '⏱ '}{label}
                </button>
              );
            })}
            {feedMode === 'batch' && (
              <>
                <span style={{ fontSize:11,color:'#7090a0',marginLeft:8 }}>Every</span>
                <select value={refreshMinutes} onChange={e => setRefreshMinutes(Number(e.target.value))}
                  style={{ background:'#060b10',border:'1px solid #1a2a3a',borderRadius:6,padding:'4px 8px',fontSize:11,color:'#c8d6e0' }}>
                  {[1,5,15,30,60].map(m => <option key={m} value={m}>{m} min</option>)}
                </select>
                {nextTickAt && (() => {
                  const secLeft = Math.max(0, Math.floor((nextTickAt - nowTs) / 1000));
                  const mm = Math.floor(secLeft / 60), ss = secLeft % 60;
                  return <span style={{ fontSize:11,color:'#f5a623',marginLeft:'auto',fontFamily:"'IBM Plex Mono',monospace" }}>Next update in {mm}:{ss.toString().padStart(2,'0')}</span>;
                })()}
                <button onClick={() => { setFeedSnapshot(assetMovementsRef.current); setNextTickAt(Date.now() + refreshMinutes * 60_000); }}
                  style={{ fontSize:11,padding:'5px 10px',borderRadius:6,background:'#1a2a3a',border:'none',color:'#c8d6e0',cursor:'pointer',marginLeft: nextTickAt ? 8 : 'auto' }}>↻ Refresh now</button>
              </>
            )}
          </div>

          {/* Live indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ width: 8, height: 8, borderRadius: '50%', background: ACCENT,
              animation: bridgeStatus?.connectedReaders > 0 ? 'ping 1.5s infinite' : 'none' }}/>
            <span style={{ fontSize: 11, color: '#7090a0' }}>
              {bridgeStatus?.connectedReaders > 0 ? `Live — ${liveCount} events this session` : 'Showing historical data'}
            </span>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 10,
              background: rtStatus === 'online' ? '#00e5a015' : rtStatus === 'polling' ? '#f5a62315' : '#7090a015',
              color:      rtStatus === 'online' ? '#00e5a0'   : rtStatus === 'polling' ? '#f5a623'   : '#7090a0',
              border:     `1px solid ${rtStatus === 'online' ? '#00e5a040' : rtStatus === 'polling' ? '#f5a62340' : '#7090a040'}`,
            }}>
              {rtStatus === 'online' ? '⚡ Realtime' : rtStatus === 'polling' ? '↻ Polling 10s' : '… Connecting'}
            </span>
          </div>

          {/* Feed */}
          <div ref={feedRef} style={{ display: 'flex', flexDirection: 'column', gap: 6,
            flex: 1, overflowY: 'auto', transition: 'box-shadow 0.3s', borderRadius: 10, minHeight: 0 }}>
            {loading ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#7090a0' }}>Loading…</div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '60px 0', color: '#7090a0' }}>
                <div style={{ fontSize: 36, marginBottom: 12 }}>📡</div>
                {loadError ? (
                  <>
                    <div style={{ fontSize: 13, color: '#e05757', marginBottom: 6 }}>Query error</div>
                    <div style={{ fontSize: 11, fontFamily: "'IBM Plex Mono',monospace", color: '#e05757', maxWidth: 400, margin: '0 auto' }}>{loadError}</div>
                  </>
                ) : (
                  <>
                    <div style={{ fontSize: 14, marginBottom: 6 }}>No movements recorded</div>
                    {diag && diag.otherOrgs.length > 0 ? (
                      <div style={{ maxWidth: 460, margin: '10px auto 0', textAlign: 'left',
                        background: '#f5a62310', border: '1px solid #f5a62330', borderRadius: 8, padding: '12px 14px' }}>
                        <div style={{ fontSize: 12, color: '#f5a623', fontWeight: 700, marginBottom: 8 }}>
                          ⚠ Org ID mismatch detected
                        </div>
                        <div style={{ fontSize: 11, color: '#a0b4c0', lineHeight: 1.6 }}>
                          The bridge is writing movements, but under a different organisation than you're logged into.
                        </div>
                        <div style={{ fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", color: '#7090a0', marginTop: 8 }}>
                          You are logged in as:<br/>
                          <span style={{ color: '#5b9cf6' }}>{diag.myOrg}</span>
                        </div>
                        <div style={{ fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", color: '#7090a0', marginTop: 6 }}>
                          Bridge is writing to:<br/>
                          {diag.otherOrgs.map(o => <span key={o} style={{ color: '#00e5a0', display: 'block' }}>{o}</span>)}
                        </div>
                        <div style={{ fontSize: 11, color: '#a0b4c0', marginTop: 10, lineHeight: 1.5 }}>
                          Fix: set the bridge's <code style={{ color: '#f5a623' }}>org_id</code> in its <code style={{ color: '#f5a623' }}>config.json</code> to match the one above, or log in with an account in that org.
                        </div>
                      </div>
                    ) : diag && diag.usedAdmin ? (
                      <div style={{ fontSize: 12, marginTop: 8 }}>
                        No movement rows exist anywhere yet — the bridge isn't writing to this Supabase.
                        <div style={{ fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", color: '#3a5a6a', marginTop: 6 }}>your org: {diag.myOrg}</div>
                      </div>
                    ) : diag ? (
                      <div style={{ fontSize: 12, marginTop: 8 }}>
                        Your org has no movements. Other orgs can't be checked from here (RLS), so this is likely an org mismatch — log in as the org the bridge writes to.
                        <div style={{ fontSize: 10, fontFamily: "'IBM Plex Mono',monospace", color: '#3a5a6a', marginTop: 6 }}>your org: {diag.myOrg}</div>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, marginTop: 8 }}>RFID bridge must be running and scanning tags to populate this feed.</div>
                    )}
                  </>
                )}
              </div>
            ) : (<>
              {filteredWithPrev.slice(0, visibleCount).map((m, i) => (
                <MovementRow getAntennaName={getAntennaName} getReaderName={getReaderName} resolveFacility={resolveFacility} key={m.id || i} m={m} fmtTime={fmtTime} fmtAgo={fmtAgo} isNew={i === 0 && liveCount > 0} />
              ))}
              {filtered.length > visibleCount && (
                <button onClick={() => setVisibleCount(c => c + 50)}
                  style={{ margin:'8px 0 4px',padding:'10px',borderRadius:8,background:'#0d1821',border:'1px solid #1a2a3a',
                    color:'#a0b4c0',fontSize:12,cursor:'pointer',width:'100%',textAlign:'center' }}>
                  ⬇ Show more ({filtered.length - visibleCount} remaining)
                </button>
              )}
              {filtered.length > 0 && filtered.length <= visibleCount && (
                <div style={{ textAlign:'center',padding:'10px 0',fontSize:11,color:'#3a5060' }}>
                  — All {filtered.length} records shown —
                </div>
              )}
            </>)}
          </div>
        </div>

        {/* Right panel */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>

          {/* Online Readers */}
          <div style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e8edf2', marginBottom: 12 }}><Radio size={13} style={{verticalAlign:'-2px',marginRight:6}}/>Antennas</div>
            {sidebarAntennas.length === 0 ? (
              <div style={{ fontSize: 12, color: '#7090a0' }}>No antennas configured</div>
            ) : sidebarAntennas.map(a => {
              const facName = resolveFacility(a.facility_id) || a.facility_name || '—';
              // Derive Online from recent tag activity (last 10 min) — bridge doesn't touch antennas.status
              const cutoff = Date.now() - 10 * 60_000;
              const active = assetMovements.some(m =>
                String(m.to_facility) === String(a.facility_id) &&
                String(m.antenna_port) === String(a.antenna_port) &&
                new Date(m.detected_at).getTime() > cutoff
              );
              return (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 0', borderBottom: '1px solid #0d1520' }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                    background: active ? ACCENT : '#e05757',
                    boxShadow: active ? `0 0 6px ${ACCENT}` : 'none' }}/>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0' }}>{a.location || `ANT${a.antenna_port}`}</div>
                    <div style={{ fontSize: 10, color: '#7090a0', marginTop: 2 }}>ANT{a.antenna_port || '?'} · {facName}</div>
                  </div>
                  <div style={{ fontSize: 10, color: '#7090a0', fontFamily: "'IBM Plex Mono',monospace" }}>
                    {(a.reads || 0).toLocaleString()}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Unknown Tags — always shown so the module never "disappears" when empty */}
          <div style={{ background: '#060b10', border: `1px solid ${unknownTags.length > 0 ? '#f5a62330' : '#1a2a3a'}`, borderRadius: 10, padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: unknownTags.length > 0 ? 12 : 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: unknownTags.length > 0 ? '#f5a623' : '#7090a0' }}>⚠ Unknown Tags ({unknownTags.length})</div>
              </div>
              {unknownTags.length === 0 ? (
                <div style={{ fontSize: 11, color: '#7090a0', marginTop: 8 }}>
                  No unknown tags right now. Tags detected by the bridge that aren't registered as assets will appear here.
                </div>
              ) : (<>
              <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 10 }}>
                Tags detected but not registered in WMS. Register them as assets to start tracking.
              </div>
              {unknownTags.slice(0, 8).map(t => {
                const lastMove = assetMovements.find(m => m.epc === t.epc);
                return (
                  <div key={t.id} style={{ display:'flex', alignItems:'center', gap:10,
                    padding: '8px 0', borderBottom: '1px solid #0d1520' }}>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize: 11, fontFamily: "'IBM Plex Mono',monospace", color: '#f5a623', marginBottom:3 }}>
                        {t.epc}
                      </div>
                      <div style={{ display:'flex', gap:10, fontSize: 10, color: '#7090a0', flexWrap:'wrap' }}>
                        <span>🕐 {fmtAgo(t.last_seen)}</span>
                        {lastMove && lastMove.antenna_port && (
                          <span style={{ color:'#00e5a0' }}>
                            📍 {getAntennaName(lastMove)}
                          </span>
                        )}
                        {lastMove?.to_facility_name && (
                          <span style={{ color:'#5b9cf6' }}>🏭 {lastMove.to_facility_name}</span>
                        )}
                      </div>
                    </div>
                    <button onClick={() => openRegister(t.epc)}
                      style={{ flexShrink: 0, fontSize: 10, padding: '4px 10px', borderRadius: 6,
                        background: '#00e5a015', border: '1px solid #00e5a040',
                        color: '#00e5a0', cursor: 'pointer' }}>
                      + Register
                    </button>
                  </div>
                );
              })}
              {unknownTags.length > 8 && (
                <div style={{ fontSize: 11, color: '#7090a0', marginTop: 8, textAlign: 'center' }}>
                  +{unknownTags.length - 8} more unknown tags
                </div>
              )}
              </>)}
          </div>

          {/* Assets by Facility */}
          <div style={{ background: '#060b10', border: '1px solid #1a2a3a', borderRadius: 10, padding: '14px 16px' }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#e8edf2', marginBottom: 12 }}>📦 Assets by Facility</div>
            {facilities.map(f => {
              const facAssets  = assets.filter(a => a.currentFacility === f.id && a.status !== 'Decommissioned');
              const facAnts    = antennas.filter(a => String(a.facility_id) === String(f.id));
              if (facAssets.length === 0) return null;
              return (
                <div key={f.id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: '#c8d6e0' }}>{f.name}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: ACCENT,
                      fontFamily: "'IBM Plex Mono',monospace" }}>{facAssets.length} assets</div>
                  </div>
                  {facAnts.length > 0 && (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {facAnts.map(ant => {
                        const atAnt = assetMovements.filter(m =>
                          m.to_facility === f.id && m.antenna_port === ant.antenna_port
                        ).length;
                        return (
                          <div key={ant.id} style={{ display: 'flex', alignItems: 'center', gap: 4,
                            padding: '3px 8px', borderRadius: 6,
                            background: ant.status === 'Online' ? '#00e5a010' : '#1a2a3a',
                            border: `1px solid ${ant.status === 'Online' ? '#00e5a030' : '#2a3a4a'}`,
                            fontSize: 10 }}>
                            <span style={{ color: '#c8d6e0', fontWeight: 600 }}>{ant.location}</span>
                            <span style={{ fontFamily: "'IBM Plex Mono',monospace", color: '#7090a0', fontSize: 10 }}>ANT{ant.antenna_port}</span>
                            {atAnt > 0 && <span style={{ color: ACCENT, fontWeight: 700 }}>{atAnt}</span>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
            {assets.length === 0 && (
              <div style={{ fontSize: 12, color: '#7090a0' }}>No assets tracked yet</div>
            )}
          </div>

        </div>
      </div>
    </div>

    {/* Register Unknown EPC Modal */}
    {registerEpc && (
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
        onClick={(e) => { if (e.target === e.currentTarget) setRegisterEpc(null); }}>
        <div style={{ background: '#090e15', border: '1px solid #1a2a3a', borderRadius: 14,
          padding: 28, width: '100%', maxWidth: 440 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#e8edf2', marginBottom: 4, fontFamily: "'Syne',sans-serif" }}>
            Register Asset
          </div>
          <div style={{ fontSize: 12, color: '#7090a0', marginBottom: 20 }}>
            Create a new asset record for this unknown EPC tag
          </div>

          {/* EPC (readonly) */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 6 }}>EPC Tag</div>
            <div style={{ fontSize: 12, fontFamily: "'IBM Plex Mono',monospace", color: '#f5a623',
              background: '#0d1821', border: '1px solid #1a2a3a', padding: '8px 12px',
              borderRadius: 8, letterSpacing: '0.5px' }}>
              {registerEpc}
            </div>
          </div>

          {/* Asset Name */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 6 }}>Asset Name <span style={{ color: '#e05757' }}>*</span></div>
            <input
              autoFocus
              style={{ width: '100%', background: '#0d1821', border: `1px solid ${registerError && !registerForm.name.trim() ? '#e05757' : '#1a2a3a'}`,
                borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#e8edf2', outline: 'none', boxSizing: 'border-box' }}
              placeholder="e.g. Laptop Dell XPS, Barcode Scanner #3…"
              value={registerForm.name}
              onChange={e => { setRegisterForm(p => ({ ...p, name: e.target.value })); setRegisterError(''); }}
              onKeyDown={e => { if (e.key === 'Enter') doRegister(); if (e.key === 'Escape') setRegisterEpc(null); }}
            />
          </div>

          {/* Type */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 6 }}>Asset Type</div>
            <select
              style={{ width: '100%', background: '#0d1821', border: '1px solid #1a2a3a',
                borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#e8edf2', outline: 'none' }}
              value={registerForm.type}
              onChange={e => setRegisterForm(p => ({ ...p, type: e.target.value }))}>
              {['Equipment','IT Asset','Furniture','Tool','Vehicle','Medical Device','Other'].map(t =>
                <option key={t} value={t}>{t}</option>
              )}
            </select>
          </div>

          {/* Facility */}
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 11, color: '#7090a0', marginBottom: 6 }}>Assign to Facility</div>
            <select
              style={{ width: '100%', background: '#0d1821', border: '1px solid #1a2a3a',
                borderRadius: 8, padding: '9px 12px', fontSize: 13, color: '#e8edf2', outline: 'none' }}
              value={registerForm.facility_id}
              onChange={e => setRegisterForm(p => ({ ...p, facility_id: e.target.value }))}>
              <option value="">— No facility —</option>
              {facilities.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          </div>

          {registerError && (
            <div style={{ fontSize: 12, color: '#e05757', marginBottom: 14, padding: '8px 12px',
              background: '#e0575715', border: '1px solid #e0575730', borderRadius: 8 }}>
              {registerError}
            </div>
          )}

          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button onClick={() => setRegisterEpc(null)}
              style={{ padding: '9px 20px', borderRadius: 8, background: 'transparent',
                border: '1px solid #1a2a3a', color: '#a0b4c0', fontSize: 13, cursor: 'pointer' }}>
              Cancel
            </button>
            <button onClick={doRegister} disabled={registering || !registerForm.name.trim()}
              style={{ padding: '9px 20px', borderRadius: 8,
                background: registerForm.name.trim() ? '#00e5a0' : '#1a2a3a',
                border: 'none', color: registerForm.name.trim() ? '#060b10' : '#7090a0',
                fontSize: 13, fontWeight: 700, cursor: registerForm.name.trim() ? 'pointer' : 'default',
                transition: 'all 0.2s' }}>
              {registering ? 'Registering…' : '+ Register Asset'}
            </button>
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function MovementRow({ m, fmtTime, fmtAgo, isNew, getAntennaName, getReaderName, resolveFacility }) {
  // Resolve current position labels
  const toLabel   = resolveFacility(m.to_facility_name   || m.to_facility);
  const toAnt     = m.antenna_port ? (getAntennaName(m) || `ANT${m.antenna_port}`) : null;

  // Previous position — prefer bridge-emitted from_* (facility hop),
  // fall back to the previous movement of the same asset (antenna hop within same facility).
  const bridgeFrom = resolveFacility(m.from_facility_name || m.from_facility);
  const prevFacLabel = bridgeFrom || (m._prev_facility_name ? resolveFacility(m._prev_facility_name) : null) || (m._prev_facility ? resolveFacility(m._prev_facility) : null);
  const prevAntPort  = m._prev_antenna_port;
  const prevAntName  = prevAntPort ? (m._prev_antenna_name || getAntennaName({ to_facility: m._prev_facility, antenna_port: prevAntPort, antenna_name: m._prev_antenna_name }) || `ANT${prevAntPort}`) : null;

  // Classify: MOVED (facility change) vs SHIFTED (same facility, different antenna) vs SEEN (first sighting)
  const facilityChanged = !!prevFacLabel && !!toLabel && prevFacLabel !== toLabel;
  const antennaChanged  = !facilityChanged && !!prevAntPort && !!m.antenna_port && String(prevAntPort) !== String(m.antenna_port);
  const eventType = facilityChanged ? 'MOVED' : antennaChanged ? 'SHIFTED' : 'SEEN';
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 12, padding: '12px 14px',
      background: isNew ? 'rgba(0,229,160,0.05)' : '#060b10',
      border: `1px solid ${isNew ? '#00e5a040' : '#1a2a3a'}`,
      borderRadius: 8, transition: 'all 0.5s',
    }}>
      {/* Time */}
      <div style={{ flexShrink: 0, textAlign: 'right', minWidth: 52 }}>
        <div style={{ fontSize: 11, color: '#00e5a0', fontFamily: "'IBM Plex Mono',monospace", fontWeight: 700 }}>
          {fmtTime(m.detected_at)}
        </div>
        <div style={{ fontSize: 10, color: '#7090a0', marginTop: 2 }}>{fmtAgo(m.detected_at)}</div>
        {m._seenCount > 1 && (
          <div style={{ fontSize: 9, color:'#f5a623', marginTop: 3, fontWeight:700 }}>seen {m._seenCount}×</div>
        )}
      </div>

      {/* Icon */}
      <div style={{ width: 32, height: 32, borderRadius: 8, background: '#1a2a3a',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 }}>
        📦
      </div>

      {/* Details */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: '#e8edf2',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {m.asset_name || 'Unknown Asset'}
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 4, flexWrap: 'wrap', fontSize: 11, color: '#7090a0', alignItems:'center' }}>
          {facilityChanged && (<>
            <span>{prevFacLabel}{prevAntName ? ` · ${prevAntName}` : ''}</span>
            <span style={{ color:'#f5a623',fontWeight:700 }}>→</span>
            <span style={{ color:'#5b9cf6',fontWeight:600 }}>{toLabel}{toAnt ? ` · ${toAnt}` : ''}</span>
          </>)}
          {antennaChanged && (<>
            <span style={{ color:'#5b9cf6' }}>{toLabel}</span>
            <span>·</span>
            <span>{prevAntName}</span>
            <span style={{ color:'#f5a623',fontWeight:700 }}>→</span>
            <span style={{ color:'#00e5a0',fontWeight:600 }}>{toAnt}</span>
          </>)}
          {eventType === 'SEEN' && (
            <span style={{ color:'#5b9cf6',fontWeight:600 }}>{toLabel || '—'}{toAnt ? ` · ${toAnt}` : ''}</span>
          )}
          <span>·</span>
          <span style={{ fontFamily: "'IBM Plex Mono',monospace", fontSize: 10 }}>
            {m.epc?.slice(0, 16)}…
          </span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginTop: 3, fontSize: 10, color: '#7090a0' }}>
          <span style={{ color:'#a0b4c0' }}>📡 {getReaderName(m)}</span>
          {m.antenna_port && (
            <span style={{ color: '#00e5a0', fontWeight: 600 }}>
              📍 {getAntennaName(m) || `ANT${m.antenna_port}`}
              {getAntennaName(m) && (
                <span style={{ color: '#7090a0', fontWeight: 400, marginLeft: 4 }}>
                  (ANT{m.antenna_port})
                </span>
              )}
            </span>
          )}
          {m.rssi && <span style={{ color:'#a0b4c0' }}>{m.rssi} dBm</span>}
        </div>
      </div>

      {/* Event badge */}
      {eventType === 'MOVED' && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: '#e0575718', border: '1px solid #e0575740', color: '#e05757' }}>↔ MOVED</span>
      )}
      {eventType === 'SHIFTED' && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: '#f5a62318', border: '1px solid #f5a62340', color: '#f5a623' }}>⇄ SHIFTED</span>
      )}
      {eventType === 'SEEN' && (
        <span style={{ fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20, flexShrink: 0,
          background: '#00e5a010', border: '1px solid #00e5a030', color: '#00e5a0' }}>● SEEN</span>
      )}
    </div>
  );
}
